import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { formatTimestamp } from "@/lib/date-utils";
import { useEventFeedbackSummary } from "@/queries/event-feedback";
import { StarRow } from "./StarRow";

/**
 * Admin view of what members said about an event: the average rating, how many
 * people rated it, and every submission attributed to its author. Read-only —
 * admins never edit or delete a member's feedback from here.
 */
export function EventFeedbackCard({ eventId }: { eventId: string }) {
  const { data, isLoading } = useEventFeedbackSummary(eventId);
  const average = data?.average_rating ?? null;

  return (
    <Card id="feedback" className="scroll-mt-28">
      <CardHeader>
        <CardTitle className="text-lg">Feedback</CardTitle>
        <CardDescription>How attendees rated this event</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-muted-foreground text-sm">Loading feedback…</p>
        ) : !data || average === null ? (
          <p className="text-muted-foreground text-sm italic">No feedback yet.</p>
        ) : (
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <span className="text-3xl font-semibold tabular-nums">{average.toFixed(1)}</span>
              <div className="space-y-1">
                <StarRow
                  value={average}
                  label={`Average rating ${average.toFixed(1)} out of 5`}
                  className="size-5"
                />
                <p className="text-muted-foreground text-sm">
                  {data.count} {data.count === 1 ? "rating" : "ratings"}
                </p>
              </div>
            </div>

            <ul className="space-y-4">
              {data.submissions.map((submission) => (
                <li
                  key={submission.contact_id}
                  className="space-y-1 border-b pb-4 last:border-b-0 last:pb-0"
                >
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="font-medium">{submission.display_name}</span>
                    <StarRow
                      value={submission.rating}
                      label={`${submission.display_name} rated this ${submission.rating} out of 5`}
                    />
                    <span className="text-muted-foreground text-xs">
                      {formatTimestamp(submission.created_at)}
                    </span>
                    {submission.updated_at !== submission.created_at && (
                      <span className="text-muted-foreground text-xs">(edited)</span>
                    )}
                  </div>
                  {submission.comment && (
                    <p className="text-muted-foreground text-sm whitespace-pre-wrap">
                      {submission.comment}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
